<script>
/* eslint-disable @intlify/vue-i18n/no-raw-text */
/* eslint-disable vue/no-bare-strings-in-template */
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from '@lumeratec/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@lumeratec/utils';
import lumeratecConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  LUMERAI_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import TasksAPI from 'dashboard/api/lumerai/tasks';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    AttachmentPreview,
    AudioRecorder,
    ReplyBoxBanner,
    EmojiIconPicker,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  props: {
    officialDeliveryPending: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggleEditorSize'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const messageEditor = useTemplateRef('messageEditor');
    const copilot = useCopilotReply();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      messageEditor,
      copilot,
      shortcutKey,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      hasRecordedAudio: false,
      copilotAcceptedMessages: {},
      igReplyType: 'private',
      showCatalogSearch: false,
      catalogQuery: '',
      catalogResult: null,
      catalogSelection: {},
      isCatalogSearching: false,
      catalogCheckoutAutomatic: false,
      showCatalogCheckoutReview: false,
      catalogCheckoutReview: null,
      catalogCheckoutItems: [],
      catalogCheckoutPendingSend: null,
      catalogCheckoutAddQuery: '',
      catalogCheckoutAddResult: null,
      isCatalogCheckoutReviewing: false,
      isCatalogCheckoutAdding: false,
      isCatalogCheckoutCreating: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
    }),
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    latestMetaCatalogOrderMessage() {
      return [...(this.currentChat?.messages || [])].reverse().find(message => {
        const order = message.content_attributes?.meta_catalog_order || {};
        return Array.isArray(order.product_items) && order.product_items.length;
      });
    },
    instagramCommentMessage() {
      if (!this.isAnInstagramChannel) return null;

      return [...(this.currentChat?.messages || [])].reverse().find(message => {
        const attributes = message.content_attributes || {};
        return (
          attributes.is_instagram_comment &&
          (attributes.ig_comment_id || message.source_id)
        );
      });
    },
    instagramCommentExternalId() {
      return (
        this.instagramCommentMessage?.content_attributes?.ig_comment_id ||
        this.instagramCommentMessage?.source_id ||
        null
      );
    },
    isInstagramCommentConversation() {
      return Boolean(
        this.isAnInstagramChannel && this.instagramCommentExternalId
      );
    },
    hasInstagramDirectActivity() {
      if (!this.isInstagramCommentConversation) return false;

      return (this.currentChat?.messages || []).some(message => {
        const attributes = message.content_attributes || {};
        const isPrivateReply = attributes.ig_reply_type === 'private';
        const isIncomingDirect =
          message.message_type === 0 &&
          !message.private &&
          !attributes.is_instagram_comment &&
          !attributes.is_instagram_post_context;

        return isPrivateReply || isIncomingDirect;
      });
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isActive.value
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    isPrivate() {
      if (
        this.currentChat.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      ) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    hasMeaningfulEditorContent() {
      const body = this.message || '';
      // Only strip the signature when it's actually being auto-appended.
      // If the toggle is off, the agent's text might happen to match their
      // saved signature and we'd incorrectly treat it as empty.
      const shouldStripSignature =
        !this.isPrivate && this.sendWithSignature && !!this.messageSignature;
      if (!shouldStripSignature) return !!body.trim();
      const stripped = removeSignature(
        body,
        this.messageSignature,
        getEffectiveChannelType(this.channelType, this.inbox?.medium || '')
      );
      return !!stripped.trim();
    },
    isReplyRestricted() {
      return (
        !this.currentChat?.can_reply &&
        !(this.isAWhatsAppChannel || this.isAPIInbox)
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (!this.isPrivate && this.inbox?.sending_enabled === false) {
        return this.inbox.sending_blocked_reason;
      }
      if (this.isEditorDisabled) {
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }

      if (!this.isPrivate && this.isInstagramCommentConversation) {
        const contactName = this.currentContact?.name || 'cliente';
        return `Enviar mensagem direta (DM) para ${contactName}...`;
      }

      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    editorAriaLabel() {
      if (this.isPrivate) {
        return this.$t('CONVERSATION.REPLYBOX.EDITOR_PRIVATE_LABEL');
      }
      if (this.isInstagramCommentConversation) {
        return this.$t('CONVERSATION.REPLYBOX.EDITOR_INSTAGRAM_DIRECT_LABEL');
      }
      return this.$t('CONVERSATION.REPLYBOX.EDITOR_PUBLIC_LABEL');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      const { image_send: imageSend } =
        this.currentChat?.additional_attributes?.tiktok_capabilities ?? {};
      const tiktokAttachmentSupported = imageSend ?? true;

      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        (this.isATiktokChannel && tiktokAttachmentSupported)
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = lumeratecConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppCloudChannel) {
        return AUDIO_FORMATS.OGG;
      }
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    templateContextVariables() {
      const contact = this.currentContact || {};
      return {
        nome: contact.name,
        telefone: contact.phone_number || contact.phoneNumber,
        email: contact.email,
      };
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return this.isAnEmailChannel && !this.isOnPrivateNote;
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    isLumeraiHandoffAwaitingAssumption() {
      return (
        this.currentChat?.custom_attributes?.ai_autopilot_status ===
          'handed_over' &&
        this.currentChat?.meta?.assignee?.id !== this.currentUser?.id
      );
    },
    requiresAssumptionBeforeReply() {
      return this.currentChat?.meta?.assignee?.id !== this.currentUser?.id;
    },
    isMessagingWindowRestricted() {
      return (
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.currentChat.can_reply
      );
    },
    isAwaitingActiveMessageReply() {
      const lastPublicMessage = [...(this.currentChat?.messages || [])]
        .reverse()
        .find(message => !message.private && message.message_type !== 2);
      const isOutgoing =
        lastPublicMessage?.message_type === 1 ||
        lastPublicMessage?.message_type === 'outgoing';
      const hasTemplateParameters = Boolean(
        lastPublicMessage?.additional_attributes?.template_params ||
          lastPublicMessage?.content_attributes?.template_params
      );

      return (
        this.isMessagingWindowRestricted && isOutgoing && hasTemplateParameters
      );
    },
    messagingWindowRestrictionMessage() {
      if (this.isAwaitingActiveMessageReply) {
        return 'Mensagem ativa enviada. Aguarde a resposta do destinatário para continuar a conversa.';
      }

      return this.$t('CONVERSATION.FOOTER.REPLIES_DISABLED');
    },
    isEditorDisabled() {
      if (this.isOnPrivateNote) return false;
      if (this.inbox?.sending_enabled === false) return true;

      if (this.officialDeliveryPending) return true;

      if (this.requiresAssumptionBeforeReply) return true;

      return (
        this.currentChat.status === 'resolved' ||
        this.isMessagingWindowRestricted
      );
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);
    emitter.on('restore-draft-message', this.handleRestoreDraftMessage);
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
    emitter.off('restore-draft-message', this.handleRestoreDraftMessage);
  },
  methods: {
    handleRestoreDraftMessage(content) {
      // Option 1: Only restore if the editor is currently empty or contains the same content
      if (!this.message || this.message.trim() === '') {
        this.addIntoEditor(content);
      }
    },
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.replyType
    ) {
      return `draft-${conversationId}-${replyType}`;
    },
    getCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      // Even when editor is disabled (e.g. WhatsApp/API can't reply), we must
      // still normalize stale signatures out of drafts when signature is off.
      if (this.isEditorDisabled && this.sendWithSignature) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );

      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.copilot.isActive.value) {
              this.onSubmitCopilotReply();
            } else if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // Instagram and TikTok do not support sending text and attachments in the same message.
        // For Instagram, combining them causes duplicate messages due to separate echo events per component.
        // For TikTok, the API rejects messages that mix text and media.
        // To handle both cases, text and attachments are always sent as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        const isOnTiktok = this.isATiktokChannel;
        if ((isOnWhatsApp || isOnInstagram || isOnTiktok) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(
            this.message,
            copilotAcceptedMessage
          );
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(
            messagePayload,
            this.message,
            copilotAcceptedMessage
          );
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
      }
    },
    sendMessageAsMultipleMessages(message, copilotAcceptedMessage = '') {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(
          messagePayload,
          messagePayload.message || '',
          copilotAcceptedMessage
        );
      });
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(LUMERAI_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        if (
          messagePayload.contentAttributes?.shopify_meta_catalog_manual_checkout
        ) {
          this.catalogCheckoutPendingSend = null;
        }
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    onReplaceWhatsAppReply(messagePayload) {
      this.message = messagePayload.message || '';
      this.hideWhatsappTemplatesModal();
      this.$nextTick(() => {
        this.messageEditor?.focusEditorInputField();
      });
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox)
        this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    async selectEmojiOrSticker(selection) {
      if (selection.type !== 'sticker') {
        this.addIntoEditor(selection.value);
        return;
      }

      try {
        const response = await window.axios.get(selection.sticker.file_url, {
          responseType: 'blob',
        });
        const filename = `staff-sticker-${selection.sticker.id}-${
          selection.sticker.filename || `${selection.sticker.name}.webp`
        }`;
        const file = new File([response.data], filename, {
          type: response.data.type || 'image/webp',
        });
        const stickerUpload = {
          file,
          name: file.name,
          type: file.type,
          size: file.size,
          isSticker: true,
        };

        if (await this.sendStickerImmediately(stickerUpload)) {
          this.hideEmojiPicker();
        }
      } catch (error) {
        useAlert('Não foi possível adicionar a figurinha à mensagem.');
      }
    },
    async sendStickerImmediately(stickerUpload) {
      if (this.isPrivate) {
        useAlert('Figurinhas só podem ser enviadas em respostas públicas.');
        return false;
      }

      const payload = {
        conversationId: this.currentChat.id,
        files: [stickerUpload.file],
        private: false,
        message: '',
        sender: this.sender,
        contentType: 'sticker',
      };

      await this.$store.dispatch('createPendingMessageAndSend', payload);
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      emitter.emit(BUS_EVENTS.MESSAGE_SENT);
      this.sendMessageAnalyticsData(false);
      return true;
    },
    executeCopilotAction(action, data) {
      if (action === 'catalog_search') {
        this.showCatalogSearch = true;
        this.showCatalogCheckoutReview = false;
        this.catalogResult = null;
        this.catalogSelection = {};
        // Each new catalog starts in the safest mode. Without this reset, a
        // previous automatic checkout choice leaked into a later manual
        // catalog even when the attendant intended to review it first.
        this.catalogCheckoutAutomatic = false;
        return;
      }
      if (action === 'catalog_checkout_review') {
        this.openCatalogCheckoutReview();
        return;
      }
      this.copilot.execute(action, data);
    },
    async openCatalogCheckoutReview() {
      const sourceMessage = this.latestMetaCatalogOrderMessage;
      if (!sourceMessage?.id) {
        useAlert(
          'Ainda não há uma seleção de catálogo do cliente nesta conversa.'
        );
        return;
      }
      this.showCatalogSearch = false;
      this.showCatalogCheckoutReview = true;
      this.catalogCheckoutReview = null;
      this.isCatalogCheckoutReviewing = true;
      try {
        const { data } = await TasksAPI.catalogCheckoutReview({
          conversationId: this.currentChat.id,
          sourceMessageId: sourceMessage.id,
        });
        this.catalogCheckoutReview = data;
        this.catalogCheckoutItems = data.already_checked_out
          ? []
          : data.review_items || [];
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível validar a seleção agora.'
        );
      } finally {
        this.isCatalogCheckoutReviewing = false;
      }
    },
    async createCatalogCheckout() {
      const sourceMessageId = this.catalogCheckoutReview?.source_message_id;
      if (!sourceMessageId) return;
      this.isCatalogCheckoutCreating = true;
      try {
        const { data } = await TasksAPI.catalogCheckoutCreate({
          conversationId: this.currentChat.id,
          sourceMessageId,
          items: this.catalogCheckoutItems.map(item => ({
            variant_id: item.variant_id,
            quantity: item.quantity,
          })),
        });
        this.catalogCheckoutReview = { ...this.catalogCheckoutReview, ...data };
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível gerar o checkout agora.'
        );
      } finally {
        this.isCatalogCheckoutCreating = false;
      }
    },
    removeCatalogCheckoutItem(index) {
      this.catalogCheckoutItems.splice(index, 1);
    },
    async searchCatalogCheckoutAddition() {
      const query = this.catalogCheckoutAddQuery.trim();
      if (!query) return;
      this.isCatalogCheckoutAdding = true;
      try {
        const { data } = await TasksAPI.catalogPreview({
          query,
          conversationId: this.currentChat.id,
        });
        this.catalogCheckoutAddResult = data;
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível consultar o catálogo agora.'
        );
      } finally {
        this.isCatalogCheckoutAdding = false;
      }
    },
    addCatalogCheckoutItem(product, variant) {
      if (!variant?.id) return;
      const existing = this.catalogCheckoutItems.find(
        item => item.variant_id === variant.id
      );
      if (existing) {
        existing.quantity += 1;
        return;
      }
      this.catalogCheckoutItems.push({
        product_id: product.id,
        product_title: product.title,
        variant_id: variant.id,
        variant_title: variant.title,
        quantity: 1,
        price: variant.price,
        currency_code: variant.currency_code,
      });
    },
    insertCatalogCheckoutLink() {
      const url = this.catalogCheckoutReview?.checkout_url;
      if (!url) return;
      this.message = `✅ Seu carrinho está pronto. Finalize endereço, frete e pagamento com segurança:\n${url}`;
      this.catalogCheckoutPendingSend = {
        sourceMessageId: this.catalogCheckoutReview.source_message_id,
        checkoutUrl: url,
      };
      this.showCatalogCheckoutReview = false;
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    async searchCatalogForCustomer() {
      const query = this.catalogQuery.trim();
      if (!query) {
        useAlert('Digite o produto que deseja procurar.');
        return;
      }
      this.isCatalogSearching = true;
      this.catalogResult = null;
      try {
        const { data } = await TasksAPI.catalogPreview({
          query,
          conversationId: this.currentChat.id,
        });
        this.catalogResult = data;
        this.addSearchResultsToCatalogSelection(data);
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível consultar o catálogo agora.'
        );
      } finally {
        this.isCatalogSearching = false;
      }
    },
    async sendNativeCatalogToCustomer() {
      const selectedEntries = Object.values(this.catalogSelection);
      const selectedItems = selectedEntries
        .map(entry => entry.nativeItem)
        .filter(Boolean);
      if (!selectedItems.length) {
        useAlert('Selecione ao menos um produto disponível para enviar.');
        return;
      }

      const selectedProductsByRetailerId = Object.fromEntries(
        selectedEntries
          .filter(entry => entry.nativeItem)
          .map(entry => [entry.nativeItem.product_retailer_id, entry.product])
      );
      const selectedCatalog = {
        catalog_id: selectedEntries[0].catalogId,
        items: selectedItems,
        products_by_retailer_id: selectedProductsByRetailerId,
        session_expires_at: selectedEntries[0].sessionExpiresAt,
      };

      await this.sendMessage({
        conversationId: this.currentChat.id,
        private: false,
        message: `Encontrei ${selectedItems.length} ${selectedItems.length === 1 ? 'modelo' : 'modelos'} para você.`,
        sender: this.sender,
        contentAttributes: {
          shopify_meta_catalog: selectedCatalog,
          shopify_meta_catalog_checkout_mode: this.catalogCheckoutAutomatic
            ? 'automatic'
            : 'agent_review',
        },
      });
      await this.sendMessage({
        conversationId: this.currentChat.id,
        private: true,
        message: this.catalogSelectionAuditNote(selectedEntries),
        sender: this.sender,
        contentAttributes: {
          shopify_meta_catalog_sent_by_agent: true,
          shopify_meta_catalog_checkout_mode: this.catalogCheckoutAutomatic
            ? 'automatic'
            : 'agent_review',
          catalog_product_ids: selectedEntries.map(entry => entry.product.id),
        },
      });
      this.showCatalogSearch = false;
      this.catalogResult = null;
      this.catalogSelection = {};
    },
    catalogSelectionAuditNote(entries) {
      const items = entries.map(entry => {
        const variant = entry.product.variants?.[0];
        const price = variant
          ? ` — ${variant.currency_code || 'USD'} ${variant.price}`
          : '';
        const options = (entry.product.options || [])
          .map(option => `${option.name}: ${option.values.join(', ')}`)
          .join(' · ');
        return `• ${entry.product.title}${price}${options ? `\n  ${options}` : ''}`;
      });
      const mode = this.catalogCheckoutAutomatic
        ? 'Checkout automático ativado: ao selecionar, o cliente recebe o link depois da validação de estoque e preço.'
        : 'Revisão do atendente: ao selecionar, o cliente não recebe link até que o atendente revise e gere o checkout.';
      return `📦 Catálogo personalizado enviado pelo atendente\n\n${items.join('\n')}\n\n${mode}`;
    },
    addSearchResultsToCatalogSelection(result) {
      const catalog = result.native_catalog;
      (result.products || []).forEach(product => {
        const nativeItem = (catalog?.items || []).find(item => {
          const nativeProduct =
            catalog.products_by_retailer_id?.[item.product_retailer_id];
          return nativeProduct?.id?.toString() === product.id.toString();
        });
        if (nativeItem) {
          this.catalogSelection[product.id.toString()] = {
            product,
            nativeItem,
            catalogId: catalog.catalog_id,
            sessionExpiresAt: catalog.session_expires_at,
          };
        }
      });
    },
    toggleCatalogProduct(product) {
      const productId = product.id.toString();
      if (this.catalogSelection[productId]) {
        delete this.catalogSelection[productId];
        return;
      }

      const catalog = this.catalogResult?.native_catalog;
      const nativeItem = (catalog?.items || []).find(item => {
        const nativeProduct =
          catalog.products_by_retailer_id?.[item.product_retailer_id];
        return nativeProduct?.id?.toString() === productId;
      });
      if (nativeItem) {
        this.catalogSelection[productId] = {
          product,
          nativeItem,
          catalogId: catalog.catalog_id,
          sessionExpiresAt: catalog.session_expires_at,
        };
      }
    },
    clearMessage() {
      this.message = '';
      this.clearCopilotAcceptedMessage();
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isVoiceMessage to the file to identify recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isVoiceMessage: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    onRecordError({ error } = {}) {
      this.toggleAudioRecorder();
      const isMicrophonePermissionError =
        error?.name === 'NotAllowedError' ||
        error?.name === 'SecurityError' ||
        /permission denied/i.test(error?.message || '');
      if (isMicrophonePermissionError) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_PERMISSION'));
        return;
      }

      useAlert(this.$t('CONVERSATION.REPLYBOX.AUDIO_CONVERSION_FAILED'));
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file, thumb }) {
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      const attach = preview => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: preview,
          blobSignedId: blob ? blob.signed_id : undefined,
          isVoiceMessage: file?.isVoiceMessage || false,
          isSticker: file?.isSticker || false,
        });
      };

      if (thumb) {
        attach(thumb);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        attach(reader.result);
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      let payloadWithReply = payload;

      if (this.inReplyTo?.id) {
        let contentAttributes = {
          ...payload.contentAttributes,
          in_reply_to: this.inReplyTo.id,
        };

        if (this.inReplyTo.content_attributes?.is_instagram_comment) {
          contentAttributes.in_reply_to_external_id = this.inReplyTo.source_id;
          contentAttributes.ig_reply_type = this.igReplyType;
        }

        payloadWithReply = {
          ...payload,
          contentAttributes: contentAttributes,
        };
      }

      const shouldStartPrivateReplyFromComment =
        !payloadWithReply.private &&
        this.isInstagramCommentConversation &&
        !this.hasInstagramDirectActivity &&
        !payloadWithReply.contentAttributes?.in_reply_to_external_id;

      if (!shouldStartPrivateReplyFromComment) return payloadWithReply;

      return {
        ...payloadWithReply,
        contentAttributes: {
          ...payloadWithReply.contentAttributes,
          ig_reply_type: 'private',
          in_reply_to_external_id: this.instagramCommentExternalId,
        },
      };
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption =
          this.isAnInstagramChannel || this.isATiktokChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
            isVoiceMessage: attachment.isVoiceMessage || false,
            contentType: attachment.isSticker ? 'sticker' : undefined,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram and TikTok, text must always be sent as a separate message (no captions on attachments).
      // For WhatsApp, we only need a text message if there are no attachments.
      if (
        ((this.isAnInstagramChannel || this.isATiktokChannel) &&
          this.message) ||
        (!(this.isAnInstagramChannel || this.isATiktokChannel) &&
          hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload.contentAttributes =
          this.catalogCheckoutAttributesFor(message);

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload.contentAttributes =
        this.catalogCheckoutAttributesFor(messageWithQuote);
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        messagePayload.contentType =
          this.attachedFiles.length === 1 && this.attachedFiles[0].isSticker
            ? 'sticker'
            : undefined;
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
            if (attachment.isVoiceMessage) {
              messagePayload.isVoiceMessage = true;
            }
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    catalogCheckoutAttributesFor(message) {
      const pending = this.catalogCheckoutPendingSend;
      if (
        this.isPrivate ||
        !pending?.sourceMessageId ||
        !pending.checkoutUrl ||
        !message?.includes(pending.checkoutUrl)
      ) {
        return undefined;
      }

      return {
        shopify_meta_catalog_manual_checkout: true,
        source_message_id: pending.sourceMessageId,
        checkout_url: pending.checkoutUrl,
      };
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    onReplyToMessage() {
      this.fetchAndSetReplyTo();
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = this.isSignatureEnabledForInbox ? 'start' : 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isVoiceMessage
      );
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    async onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      let finalMessage = acceptedMessage;

      const regex = /!\[([^\]]*)\]\(([^)]+)\)/g;
      const imageUrls = Array.from(acceptedMessage.matchAll(regex)).map(
        match => match[2]
      );

      if (imageUrls.length > 0) {
        await Promise.all(
          imageUrls.map(async url => {
            try {
              const response = await fetch(url);
              if (response.ok) {
                let blob = await response.blob();
                let filename =
                  url.split('/').pop().split('?')[0] || 'image.jpg';
                let blobType = blob.type;
                if (!blobType || !blobType.startsWith('image/')) {
                  const ext = filename.split('.').pop().toLowerCase();
                  if (ext === 'png') blobType = 'image/png';
                  else if (ext === 'gif') blobType = 'image/gif';
                  else if (ext === 'webp') blobType = 'image/webp';
                  else if (ext === 'bmp') blobType = 'image/bmp';
                  else blobType = 'image/jpeg'; // default for markdown images
                }

                if (blobType === 'image/svg+xml') {
                  try {
                    blob = await new Promise((resolve, reject) => {
                      const img = new Image();
                      const objectUrl = URL.createObjectURL(blob);
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width || 800;
                        canvas.height = img.height || 600;
                        const ctx = canvas.getContext('2d');
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        ctx.drawImage(img, 0, 0);
                        canvas.toBlob(pngBlob => {
                          URL.revokeObjectURL(objectUrl);
                          resolve(pngBlob);
                        }, 'image/png');
                      };
                      img.onerror = () => {
                        URL.revokeObjectURL(objectUrl);
                        reject(new Error('SVG to PNG conversion failed'));
                      };
                      img.src = objectUrl;
                    });
                    blobType = 'image/png';
                    filename = filename.replace(/\.svg$/i, '.png');
                    if (!filename.endsWith('.png')) filename += '.png';
                  } catch (err) {
                    // Fallback to original blob if conversion fails
                  }
                }

                const file = new File([blob], filename, { type: blobType });
                this.onFileUpload({
                  file,
                  name: filename,
                  type: blobType,
                  size: blob.size,
                });

                const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                finalMessage = finalMessage.replace(
                  new RegExp(`!\\[[^\\]]*\\]\\(${escapedUrl}\\)`, 'g'),
                  ''
                );
              }
            } catch (e) {
              // Ignore CORS or fetch errors
            }
          })
        );
        finalMessage = finalMessage.trim();
      }

      this.message = finalMessage;
      this.setCopilotAcceptedMessage(finalMessage);
    },
  },
};
</script>

<template>
  <div
    id="reply-composer"
    ref="replyEditor"
    class="reply-box"
    role="region"
    :aria-label="$t('ACCESSIBILITY.REPLY_COMPOSER')"
    :class="replyBoxClass"
  >
    <ReplyTopPanel
      :mode="replyType"
      :conversation-id="conversationId"
      :is-instagram-comment-conversation="isInstagramCommentConversation"
      :is-reply-restricted="isReplyRestricted"
      :disabled="
        (copilot.isActive.value && copilot.isButtonDisabled.value) ||
        showAudioRecorderEditor
      "
      :is-editor-disabled="isEditorDisabled"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :editor-content="message"
      :has-content="hasMeaningfulEditorContent"
      :is-copilot-active="copilot.isActive.value"
      @set-reply-mode="setReplyMode"
      @toggle-editor-size="toggleEditorSize"
      @toggle-copilot="copilot.toggleEditor"
      @execute-copilot-action="executeCopilotAction"
    />
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div :key="copilot.editorTransitionKey.value" class="reply-box__top">
        <ReplyToMessage
          v-if="shouldShowReplyToMessage"
          :message="inReplyTo"
          @dismiss="resetReplyToMessage"
        />

        <EmojiIconPicker
          v-if="showEmojiPicker"
          v-on-clickaway="hideEmojiPicker"
          mode="emoji"
          class="emoji-dialog"
          :class="{
            'emoji-dialog--expanded': isOnExpandedLayout,
          }"
          include-staff-stickers
          @select="selectEmojiOrSticker"
        />
        <ReplyEmailHead
          v-if="showReplyHead && isDefaultEditorMode"
          v-model:cc-emails="ccEmails"
          v-model:bcc-emails="bccEmails"
          v-model:to-emails="toEmails"
        />
        <AudioRecorder
          v-if="showAudioRecorderEditor"
          ref="audioRecorderInput"
          :audio-record-format="audioRecordFormat"
          @recorder-progress-changed="onRecordProgressChanged"
          @finish-record="onFinishRecorder"
          @record-error="onRecordError"
          @play="recordingAudioState = 'playing'"
          @pause="recordingAudioState = 'paused'"
        />
        <div
          v-if="showCatalogSearch && !showAudioRecorderEditor"
          class="mx-4 mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
        >
          <div
            class="mb-2 text-sm font-medium text-slate-800 dark:text-slate-100"
          >
            Buscar catálogo para enviar ao cliente
          </div>
          <div class="flex gap-2">
            <input
              v-model="catalogQuery"
              class="flex-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
              placeholder="Ex.: calça jeans"
              @keyup.enter="searchCatalogForCustomer"
            />
            <button
              class="rounded bg-woot-500 px-3 py-2 text-sm text-white disabled:opacity-50"
              :disabled="isCatalogSearching"
              @click="searchCatalogForCustomer"
            >
              {{ isCatalogSearching ? 'Buscando…' : 'Buscar' }}
            </button>
            <button
              class="rounded px-2 text-sm"
              @click="showCatalogSearch = false"
            >
              Fechar
            </button>
          </div>
          <label
            class="mt-3 flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200"
          >
            <input
              v-model="catalogCheckoutAutomatic"
              type="checkbox"
              class="mt-0.5 size-4"
            />
            <span>
              <span class="font-medium">Checkout automático</span><br />
              Ao receber a seleção, validamos estoque e preço e enviamos o link
              seguro. Desmarcado, você revisa e decide quando gerar o link.
            </span>
          </label>
          <div
            v-if="Object.keys(catalogSelection).length"
            class="mt-3 rounded border border-slate-200 bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <div class="mb-1 font-medium text-slate-800 dark:text-slate-100">
              Catálogo personalizado:
              {{ Object.keys(catalogSelection).length }} selecionados
            </div>
            <div class="flex flex-wrap gap-1">
              <button
                v-for="entry in Object.values(catalogSelection)"
                :key="entry.product.id"
                class="rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-700"
                @click="toggleCatalogProduct(entry.product)"
              >
                {{ entry.product.title }} ×
              </button>
            </div>
          </div>
          <template v-if="catalogResult">
            <p class="mt-3 text-sm text-slate-700 dark:text-slate-200">
              {{ catalogResult.message }}
            </p>
            <ul
              v-if="catalogResult.products?.length"
              class="mt-2 max-h-44 overflow-auto text-sm text-slate-600 dark:text-slate-300"
            >
              <li
                v-for="product in catalogResult.products"
                :key="product.id"
                class="flex items-center gap-2 py-1.5"
              >
                <input
                  type="checkbox"
                  :checked="Boolean(catalogSelection[product.id.toString()])"
                  :aria-label="`Selecionar ${product.title}`"
                  class="size-4"
                  @change="toggleCatalogProduct(product)"
                />
                <span
                  >{{ product.title }}
                  <span v-if="product.variants?.[0]"
                    >— {{ product.variants[0].currency_code || 'USD' }}
                    {{ product.variants[0].price }}</span
                  ></span
                >
              </li>
            </ul>
            <p
              v-if="catalogResult.source === 'shopify_fallback'"
              class="mt-2 text-xs text-amber-700 dark:text-amber-300"
            >
              A vitrine WhatsApp não está disponível nesta caixa. A busca serviu
              apenas para consulta; nenhum catálogo será enviado como se fosse
              nativo.
            </p>
            <button
              v-if="Object.keys(catalogSelection).length"
              class="mt-3 rounded bg-woot-500 px-3 py-2 text-sm text-white"
              @click="sendNativeCatalogToCustomer"
            >
              Enviar {{ Object.keys(catalogSelection).length }}
              {{
                Object.keys(catalogSelection).length === 1
                  ? 'modelo'
                  : 'modelos'
              }}
              no catálogo WhatsApp
            </button>
          </template>
        </div>
        <div
          v-if="showCatalogCheckoutReview && !showAudioRecorderEditor"
          class="mx-4 mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="text-sm font-medium text-slate-800 dark:text-slate-100">
              Revisar seleção do cliente
            </div>
            <button
              class="rounded px-2 text-sm"
              @click="showCatalogCheckoutReview = false"
            >
              Fechar
            </button>
          </div>
          <p
            v-if="isCatalogCheckoutReviewing"
            class="mt-3 text-sm text-slate-600 dark:text-slate-300"
          >
            Validando estoque, variações e preço na Shopify…
          </p>
          <template v-else-if="catalogCheckoutReview">
            <template v-if="catalogCheckoutReview.already_checked_out">
              <p
                class="mt-2 text-sm font-medium text-emerald-700 dark:text-emerald-300"
              >
                {{
                  catalogCheckoutReview.checkout_mode === 'automatic'
                    ? 'Checkout automático já foi enviado ao cliente para esta seleção.'
                    : 'O link do checkout já foi enviado ao cliente para esta seleção.'
                }}
              </p>
              <p class="mt-1 text-sm text-slate-700 dark:text-slate-200">
                Este carrinho está bloqueado para evitar alterações ou um
                segundo link.
              </p>
              <p
                v-if="catalogCheckoutReview.checkout_url"
                class="mt-3 break-all rounded bg-white p-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {{ catalogCheckoutReview.checkout_url }}
              </p>
            </template>
            <template v-else>
              <p class="mt-2 text-sm text-slate-700 dark:text-slate-200">
                {{ catalogCheckoutItems.length }} variações no checkout. Nada
                foi enviado ao cliente ainda.
              </p>
              <ul
                class="mt-2 max-h-44 overflow-auto text-sm text-slate-600 dark:text-slate-300"
              >
                <li
                  v-for="(item, index) in catalogCheckoutItems"
                  :key="`${item.variant_id}-${index}`"
                  class="flex items-center gap-2 py-1"
                >
                  <div class="w-14 min-w-14 max-w-14 shrink-0">
                    <input
                      v-model.number="item.quantity"
                      min="1"
                      max="20"
                      type="number"
                      class="w-full rounded border border-slate-300 bg-white px-1 py-0.5 text-sm dark:border-slate-600 dark:bg-slate-800"
                    />
                  </div>
                  <span class="min-w-0 flex-1 break-words">
                    {{ item.product_title }}
                    <span
                      v-if="
                        item.variant_title &&
                        item.variant_title !== 'Default Title'
                      "
                    >
                      — {{ item.variant_title }}
                    </span>
                    <span v-if="item.price">
                      · {{ item.currency_code || 'USD' }} {{ item.price }}
                    </span>
                  </span>
                  <button
                    class="shrink-0 rounded px-1 text-xs text-red-600"
                    :aria-label="`Remover ${item.product_title}`"
                    @click="removeCatalogCheckoutItem(index)"
                  >
                    Remover
                  </button>
                </li>
              </ul>
              <div
                class="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700"
              >
                <div
                  class="text-xs font-medium text-slate-700 dark:text-slate-200"
                >
                  Adicionar outro produto
                </div>
                <div class="mt-1 flex gap-2">
                  <input
                    v-model="catalogCheckoutAddQuery"
                    class="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800"
                    placeholder="Ex.: blazer"
                    @keyup.enter="searchCatalogCheckoutAddition"
                  />
                  <button
                    class="rounded bg-slate-200 px-2 py-1.5 text-sm dark:bg-slate-700"
                    :disabled="isCatalogCheckoutAdding"
                    @click="searchCatalogCheckoutAddition"
                  >
                    {{ isCatalogCheckoutAdding ? 'Buscando…' : 'Buscar' }}
                  </button>
                </div>
                <ul
                  v-if="catalogCheckoutAddResult?.products?.length"
                  class="mt-2 max-h-32 overflow-auto text-xs text-slate-600 dark:text-slate-300"
                >
                  <li
                    v-for="product in catalogCheckoutAddResult.products"
                    :key="product.id"
                    class="flex items-center gap-2 py-1"
                  >
                    <span class="flex-1">{{ product.title }}</span>
                    <select
                      class="max-w-44 rounded border border-slate-300 bg-white px-1 py-0.5 dark:border-slate-600 dark:bg-slate-800"
                      @change="
                        addCatalogCheckoutItem(
                          product,
                          product.variants.find(
                            variant => variant.id === $event.target.value
                          )
                        )
                      "
                    >
                      <option value="" selected>Escolher opção…</option>
                      <option
                        v-for="variant in product.variants"
                        :key="variant.id"
                        :value="variant.id"
                      >
                        {{ variant.title }} —
                        {{ variant.currency_code || 'USD' }} {{ variant.price }}
                      </option>
                    </select>
                  </li>
                </ul>
              </div>
              <template v-if="catalogCheckoutReview.checkout_url">
                <p
                  class="mt-3 break-all rounded bg-white p-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  {{ catalogCheckoutReview.checkout_url }}
                </p>
                <button
                  class="mt-3 rounded bg-woot-500 px-3 py-2 text-sm text-white"
                  @click="insertCatalogCheckoutLink"
                >
                  Inserir link na resposta
                </button>
              </template>
              <button
                v-else
                class="mt-3 rounded bg-woot-500 px-3 py-2 text-sm text-white disabled:opacity-50"
                :disabled="
                  isCatalogCheckoutCreating || !catalogCheckoutItems.length
                "
                @click="createCatalogCheckout"
              >
                {{
                  isCatalogCheckoutCreating ? 'Gerando…' : 'Gerar link seguro'
                }}
              </button>
            </template>
          </template>
        </div>
        <CopilotEditorSection
          v-if="
            copilot.isActive.value &&
            !showAudioRecorderEditor &&
            !showCatalogSearch &&
            !showCatalogCheckoutReview
          "
          :show-copilot-editor="copilot.showEditor.value"
          :is-generating-content="copilot.isGenerating.value"
          :generated-content="copilot.generatedContent.value"
          :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
          @focus="onFocus"
          @blur="onBlur"
          @clear-selection="clearEditorSelection"
          @close="copilot.showEditor.value = false"
          @content-ready="copilot.setContentReady"
          @send="copilot.sendFollowUp"
        />
        <WootMessageEditor
          v-else-if="
            !showAudioRecorderEditor &&
            !isEditorDisabled &&
            !showCatalogSearch &&
            !showCatalogCheckoutReview
          "
          ref="messageEditor"
          v-model="message"
          :conversation-id="conversationId"
          :editor-id="editorStateId"
          class="input popover-prosemirror-menu"
          :is-private="isOnPrivateNote"
          :aria-label="editorAriaLabel"
          :placeholder="messagePlaceHolder"
          :update-selection-with="updateEditorSelectionWith"
          :min-height="4"
          :disabled="isEditorDisabled"
          enable-variables
          :variables="messageVariables"
          :signature="messageSignature"
          allow-signature
          :channel-type="channelType"
          :medium="inbox.medium"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
          @toggle-user-mention="toggleUserMention"
          @toggle-canned-menu="toggleCannedMenu"
          @toggle-variables-menu="toggleVariablesMenu"
          @clear-selection="clearEditorSelection"
          @execute-copilot-action="executeCopilotAction"
        />
        <div
          v-else-if="isEditorDisabled && currentChat.status === 'resolved'"
          class="flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-100 dark:border-slate-700 mx-4 mb-2 text-center text-sm text-slate-500 dark:text-slate-400"
        >
          {{ $t('CONVERSATION.FOOTER.CONVERSATION_RESOLVED') }}
        </div>
        <div
          v-else-if="isEditorDisabled && isMessagingWindowRestricted"
          class="flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-100 dark:border-slate-700 mx-4 mb-2 text-center text-sm text-slate-500 dark:text-slate-400"
        >
          {{ messagingWindowRestrictionMessage }}
        </div>
        <ReplyBoxBanner
          v-else-if="isEditorDisabled && requiresAssumptionBeforeReply"
          :message="message"
          :is-on-private-note="isOnPrivateNote"
        />
        <div
          v-else-if="isEditorDisabled && currentChat.status !== 'resolved'"
          class="flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-100 dark:border-slate-700 mx-4 mb-2 text-center text-sm text-slate-500 dark:text-slate-400"
        >
          {{ $t('CONVERSATION.FOOTER.REPLIES_DISABLED') }}
        </div>

        <QuotedEmailPreview
          v-if="shouldShowQuotedPreview && isDefaultEditorMode"
          :quoted-email-text="quotedEmailText"
          :preview-text="quotedEmailPreviewText"
          class="mb-2"
          @toggle="toggleQuotedReply"
        />

        <div
          v-if="
            hasAttachments &&
            isDefaultEditorMode &&
            (currentChat.status !== 'resolved' || isOnPrivateNote)
          "
          class="bg-transparent py-0 mb-2"
          @paste="onPaste"
        >
          <AttachmentPreview
            class="mt-2"
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
        <MessageSignatureMissingAlert
          v-if="
            isSignatureEnabledForInbox &&
            !isSignatureAvailable &&
            isDefaultEditorMode &&
            (currentChat.status !== 'resolved' || isOnPrivateNote)
          "
          class="mb-2"
        />
      </div>
    </Transition>

    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <CopilotReplyBottomPanel
        v-if="copilot.isActive.value"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
      />
      <ReplyBottomPanel
        v-else-if="currentChat.status !== 'resolved' || isOnPrivateNote"
        key="reply-bottom-panel"
        :conversation-id="conversationId"
        :enable-multiple-file-upload="enableMultipleFileUpload"
        :enable-whats-app-templates="showWhatsappTemplates"
        :inbox="inbox"
        :is-on-private-note="isOnPrivateNote"
        :is-recording-audio="isRecordingAudio"
        :is-send-disabled="isReplyButtonDisabled"
        :is-note="isPrivate"
        :is-editor-disabled="isEditorDisabled"
        :on-file-upload="onFileUpload"
        :on-send="onSendReply"
        :conversation-type="conversationType"
        :recording-audio-duration-text="recordingAudioDurationText"
        :recording-audio-state="recordingAudioState"
        :send-button-text="replyButtonLabel"
        :show-audio-recorder="showAudioRecorder"
        :show-emoji-picker="showEmojiPicker"
        :show-file-upload="showFileUpload"
        :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
        :quoted-reply-enabled="quotedReplyPreference"
        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
        :toggle-audio-recorder="toggleAudioRecorder"
        :toggle-emoji-picker="toggleEmojiPicker"
        :message="message"
        :new-conversation-modal-active="newConversationModalActive"
        @select-whatsapp-template="openWhatsappTemplateModal"
        @toggle-quoted-reply="toggleQuotedReply"
      />
    </Transition>

    <WhatsappTemplates
      :inbox-id="inbox.id"
      :context-variables="templateContextVariables"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @on-replace="onReplaceWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.reply-box {
  @apply relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-3 -mt-px;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}
</style>
